import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Select, Spin } from 'antd';
import debounce from 'lodash/debounce';
import './debounceSelect.scss';

// The assignee picker shared by the Assign, Bulk Assign and Reassign modals.
//
// `fetchOptions(query)` resolves `{ options, total }`, where each option is
// `{ value, label, email }` and `total` is how many users match `query` on the
// server (it can exceed `options.length`). Opening the dropdown loads the
// unfiltered list right away, so the user can pick without typing; typing
// refines it, debounced. Any other prop goes straight to the antd Select.

// What the dropdown shows. Every step of a load replaces the whole state, so
// the fields can never disagree (say, a failure message over fresh results).
const INITIAL_STATE = {
  fetching: false,
  failed: false,
  options: [],
  total: 0,
  query: '',
};

const pickerReducer = (state, action) => {
  switch (action.type) {
    case 'started':
      return { ...INITIAL_STATE, fetching: true, query: action.query };
    case 'loaded':
      return {
        ...state, fetching: false, failed: false, options: action.options, total: action.total,
      };
    case 'failed':
      return {
        ...state, fetching: false, failed: true, options: [], total: 0,
      };
    default:
      return state;
  }
};

const DebounceSelect = ({
  fetchOptions,
  debounceTimeout,
  onDropdownVisibleChange,
  ...props
}) => {
  const [state, dispatch] = useReducer(pickerReducer, INITIAL_STATE);
  const {
    fetching, failed, options, total, query,
  } = state;
  const fetchRef = useRef(0);
  // The query the current options were successfully loaded for; null until the
  // first load succeeds, and while a load is in flight or after it failed, so
  // reopening the picker retries.
  const loadedQueryRef = useRef(null);
  // What is in the search box right now, which can be ahead of loadedQueryRef
  // while a keystroke waits out the debounce.
  const searchTextRef = useRef('');
  const mountedRef = useRef(true);

  const loadOptions = useCallback((value) => {
    fetchRef.current += 1;
    const fetchId = fetchRef.current;
    const nextQuery = value || '';
    loadedQueryRef.current = null;
    dispatch({ type: 'started', query: nextQuery });
    // Promise.resolve().then() so a synchronous throw lands in .catch too.
    Promise.resolve()
      .then(() => fetchOptions(nextQuery))
      .then((result) => {
        // Out-of-order response from a superseded keystroke, or unmounted.
        if (fetchId !== fetchRef.current || !mountedRef.current) return;
        loadedQueryRef.current = nextQuery;
        dispatch({ type: 'loaded', options: result?.options || [], total: result?.total || 0 });
      })
      .catch(() => {
        if (fetchId !== fetchRef.current || !mountedRef.current) return;
        dispatch({ type: 'failed' });
      });
  }, [fetchOptions]);

  const debounceFetcher = useMemo(
    () => debounce(loadOptions, debounceTimeout),
    [loadOptions, debounceTimeout],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Drop a pending keystroke search when unmounting or re-creating the debounce.
  useEffect(() => () => debounceFetcher.cancel(), [debounceFetcher]);

  const handleSearch = (value) => {
    searchTextRef.current = value || '';
    debounceFetcher(value);
  };

  const handleDropdownVisibleChange = (open) => {
    if (!open) {
      // Closing always empties the search box in single mode, but picking an
      // option empties it without calling onSearch (rc-select 13), so forget
      // the typed text here, and drop any keystroke search still waiting.
      searchTextRef.current = '';
      debounceFetcher.cancel();
    }
    // Load the unfiltered list on first open, and again when the options on
    // hand are left over from a search (antd clears the typed text on select
    // and blur, but not the options). Typing into a closed picker also opens
    // it; that keystroke's search is already pending, so leave it alone.
    if (open && !searchTextRef.current && loadedQueryRef.current !== '') {
      loadOptions('');
    }
    if (onDropdownVisibleChange) onDropdownVisibleChange(open);
  };

  const emptyContent = () => {
    if (fetching) return <Spin size="small" />;
    if (failed) return <div className="assignee-select__status">Couldn&apos;t load users. Close and reopen to try again.</div>;
    if (query) return <div className="assignee-select__status">{`No users match "${query}"`}</div>;
    return <div className="assignee-select__status">No users found</div>;
  };

  // Tells the user the list is cut off, so nobody assumes a person is missing.
  const renderDropdown = (menu) => {
    if (fetching || total <= options.length) return menu;
    const hint = query
      ? `Showing ${options.length} of ${total} matches — keep typing to narrow`
      : `Showing ${options.length} of ${total} — type to search`;
    return (
      <>
        {menu}
        <div className="assignee-select__more" role="status">{hint}</div>
      </>
    );
  };

  return (
    <Select
      labelInValue
      filterOption={false}
      optionLabelProp="label"
      onSearch={handleSearch}
      notFoundContent={emptyContent()}
      dropdownRender={renderDropdown}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      {...props}
    >
      {options.map((option) => (
        <Select.Option key={option.value} value={option.value} label={option.label}>
          <div className="assignee-select__option">
            <span className="assignee-select__name">{option.label}</span>
            {option.email && option.email !== option.label && (
              <span className="assignee-select__email">{option.email}</span>
            )}
          </div>
        </Select.Option>
      ))}
    </Select>
  );
};

DebounceSelect.propTypes = {
  fetchOptions: PropTypes.func.isRequired,
  debounceTimeout: PropTypes.number,
  onDropdownVisibleChange: PropTypes.func,
};

DebounceSelect.defaultProps = {
  debounceTimeout: 250,
  onDropdownVisibleChange: undefined,
};

export default DebounceSelect;
